import {
    FC,
    MouseEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from "react";
import Table from "../../assets/components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import "../../assets/components/Table/Table_header.scss";
import ProductsSubRow from "./ProductsSubRow/ProductsSubRow";
import {
    Permissions,
    Product,
    ProductTable,
    ProjectList,
    PropsProject,
    renderSubComponentProductTable,
} from "../../models/models";
import {
    useCreateProductMutation,
    useDeleteProductMutation,
    useGetProductsListQuery,
    useGetProjectsListQuery,
    useUpdateProductMutation,
} from "../../redux/RTKQuery/api";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import Loader from "../../assets/components/Loader/Loader";
import "./Products.scss";
import RemoveButton from "../../assets/components/RemoveButton/RemoveButton";
import { OptionList, hasPermission } from "../../assets/helpers/helpers";
import ArrowSprite from "../../assets/components/Table/Img/arrow_sprite.svg";
import { useNotification } from "../../assets/hooks/useNotification";
import { actionText } from "../../text";
import TableHeader from "../../assets/components/Table/TableHeader";
import ImageSprite from "../../assets/components/ImageSprite/ImageSprite";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import CheckImg from "./Img/check.svg";
import {
    Page,
    PaginationBar,
} from "../../assets/components/PaginationBar/PaginationBar";
import {
    SearchBar,
    SearchBarDataHandler,
} from "../../assets/components/SearchBar/SearchBar";
import { toast } from "react-toastify";

type RowItemCheckboxProps = {
    value: boolean;
};

const RowItemCheckbox: FC<RowItemCheckboxProps> = ({ value }) => {
    return (
        <div className="life_time_status" style={{ width: 24, height: 24 }}>
            <ImageSprite
                iconSprite={`${CheckImg}${value ? "#checked" : "#unchecked"}`}
            />
        </div>
    );
};

type Removed = {
    productId: number;
};

const RemovedPost: FC<Removed> = ({ productId }) => {
    const [deleteProduct] = useDeleteProductMutation({
        fixedCacheKey: "delete_actual_product",
    });

    const { AttentionMessageBox } = useNotification();

    const deleteProductHandler = async () => {
        const result = await AttentionMessageBox({
            textContent: actionText("delete", "product"),
        });
        if (result === "yes") deleteProduct(productId);
    };

    return (
        <RemoveButton
            position="top_center"
            img="top"
            onClick={() => deleteProductHandler()}
        />
    );
};

const renderSubComponent: renderSubComponentProductTable<PropsProject> = ({
    row,
    subComponentProps,
    subRowHandler,
}) => {
    const closeFormHandler = (id: number) => {
        row.toggleExpanded();

        if (id === -1 && subRowHandler) {
            subRowHandler(id);
        }
    };

    return (
        <ProductsSubRow
            {...row.original}
            closeFormHandler={closeFormHandler}
            projects={subComponentProps}
        />
    );
};

const Products: FC = () => {
    const user = useAppSelector((state) => state.initializeApp);
    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.PRODUCT_DELETE
    );

    const columns: ColumnDef<Product>[] = [
        {
            header: "Activation Product Name",
            accessorKey: "name",
            cell: ({ row, getValue }) => {
                return row.original.id !== -1
                    ? getValue<string>()
                    : "Add Product";
            },
        },
        {
            header: "Display Product Name",
            accessorKey: "title",
        },
        {
            header: () => <span>Lifetime</span>,
            accessorKey: "hasLifeTime",
            cell: ({ row, getValue }) => {
                return row.original.id !== -1 ? (
                    <RowItemCheckbox value={getValue<boolean>()} />
                ) : null;
            },
        },
        {
            header: () => <span>Trial</span>,
            accessorKey: "isTrial",
            cell: ({ row, getValue }) => {
                return row.original.id !== -1 ? (
                    <RowItemCheckbox value={getValue<boolean>()} />
                ) : null;
            },
        },
        {
            id: "migrated",
            header: "Migrated Products",
            cell: ({ row }) => {
                return row.original.migratedProductsCount
                    ? row.original.migratedProductsCount
                    : "";
            },
        },
        {
            id: "expander",
            header: () => null,
            cell: ({ row }) => {
                const editButtonIcon = row.getIsExpanded()
                    ? "arrowUp"
                    : "arrowDown";
                return row.original.id !== -1 ? (
                    <button
                        className="edit_button"
                        {...{
                            onClick: () => row.getToggleExpandedHandler(),
                            style: { cursor: "pointer" },
                        }}
                    >
                        <ImageSprite
                            iconSprite={`${ArrowSprite}#${editButtonIcon}`}
                        />
                    </button>
                ) : null;
            },
        },
        {
            id: "removed",
            header: () => null,
            cell: ({ row }) => {
                return isAccessPermission && row.original.id !== -1 ? (
                    <RemovedPost productId={row.original.id} />
                ) : null;
            },
        },
    ];

    const dispatch = useAppDispatch();
    const [isNewProductFormVisible, setNewProductFormVisible] = useState(false);
    const [dataTable, setData] = useState<Product[]>([]);
    const [dataTableProps, setTableProps] = useState<ProjectList>([]);
    const loading: Loading = useAppSelector((state) => state.ui.loading);

    const pageSize: number = 30;
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);
    const [pageNumber, setCurrentPage] = useState<Page>(1);
    const [totalProducts, setTotalProducts] = useState<number>(0);

    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});

    const [
        ,
        {
            isSuccess: isSuccessDeleteProduct,
            isError: isErrorDeleteProduct,
            isLoading: isLoadingDeleteProduct,
            reset,
        },
    ] = useDeleteProductMutation({
        fixedCacheKey: "delete_actual_product",
    });

    const [
        ,
        {
            isSuccess: isSuccessUpdatedProduct,
            isError: isErrorUpdatedProduct,
            isLoading: isLoadingUpdatedProduct,
            reset: resetUpdate,
        },
    ] = useUpdateProductMutation({ fixedCacheKey: "update_actual_product" });

    const [
        ,
        {
            isSuccess: isSuccessCreatedProduct,
            isError: isErrorCreatedProduct,
            isLoading: isLoadingCreatedProduct,
            reset: resetCreate,
        },
    ] = useCreateProductMutation({ fixedCacheKey: "create_actual_product" });

    const {
        data: dataProducts,
        isLoading: isLoadingProducts,
        isSuccess: isSuccessProducts,
        isError: isErrorProducts,
    } = useGetProductsListQuery(
        {
            pageSize,
            pageNumber,
            name: quiresData.searchInput,
            projectId: quiresData.projectId,
        },
        { refetchOnFocus: true }
    );

    useEffect(() => {
        setCurrentPageSize(pageSize);
    }, [pageSize]);

    const {
        data: projectList,
        isLoading: isLoadingProjectList,
        isSuccess: isSuccessProjectList,
        isError: isErrorProjectList,
    } = useGetProjectsListQuery(
        {},
        {
            refetchOnFocus: true,
        }
    );

    const handleProjectsData = useCallback(() => {
        if (isSuccessProjectList && projectList) {
            setTableProps(projectList.projects);
        }

        if (isErrorProjectList) {
            toast.error("Error loading projects");
        }
    }, [projectList, isSuccessProjectList, isErrorProjectList]);

    const handleProductsData = useCallback(() => {
        if (isSuccessProducts && dataProducts) {
            setData(dataProducts.products);

            if (dataProducts.filteredProductsCount !== undefined) {
                setTotalProducts(dataProducts.filteredProductsCount);
            } else {
                setTotalProducts(dataProducts.totalProducts);
            }
        }

        if (isErrorProducts) {
            toast.error("Error loading products");
        }
    }, [dataProducts, isSuccessProducts, isErrorProducts]);

    const handlerDeleteProduct = useCallback(() => {
        if (isSuccessDeleteProduct) {
            toast.success("Product was successfully deleted!");
            reset();
        }
        if (isErrorDeleteProduct) {
            toast.error("Failed to delete product!");
            reset();
        }
    }, [isSuccessDeleteProduct, isErrorDeleteProduct, reset]);

    const handlerUpdateProduct = useCallback(() => {
        if (isSuccessUpdatedProduct) {
            toast.success("Product was successfully updated!");
            resetUpdate();
        }
        if (isErrorUpdatedProduct) {
            toast.error("Failed to update product!");
            resetUpdate();
        }
    }, [isSuccessUpdatedProduct, isErrorUpdatedProduct, resetUpdate]);

    const handlerCreateProduct = useCallback(() => {
        if (isSuccessCreatedProduct) {
            toast.success("Product was successfully created!");
            resetCreate();
        }
        if (isErrorCreatedProduct) {
            toast.error("Failed to create product!");
            resetCreate();
        }
    }, [isSuccessCreatedProduct, isErrorCreatedProduct, resetCreate]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isLoadingProducts ||
            isLoadingProjectList ||
            isLoadingDeleteProduct ||
            isLoadingUpdatedProduct ||
            isLoadingCreatedProduct;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [
        isLoadingDeleteProduct,
        isLoadingProducts,
        isLoadingProjectList,
        dispatch,
        isLoadingUpdatedProduct,
        isLoadingCreatedProduct,
    ]);

    // Handle data fetching and state updates
    useLayoutEffect(() => {
        handleProductsData();
        handleLoadingState();
        handleProjectsData();
        handlerDeleteProduct();
        handlerUpdateProduct();
        handlerCreateProduct();
    }, [
        handleProductsData,
        handleLoadingState,
        handleProjectsData,
        handlerDeleteProduct,
        handlerUpdateProduct,
        handlerCreateProduct,
    ]);

    const createNewProductHandler: MouseEventHandler<
        HTMLButtonElement
    > = () => {
        const newRow: Product = {
            id: -1,
            name: "Add Product",
            title: "",
            description: "",
            days: 1,
            hasLifeTime: true,
            isFree: false,
            isTrial: true,
            isDisabled: false,
            isOpen: true,
            features: [],
            projectId: 0,
            gracePeriod: 1,
            priority: 1,

            activatedLicenses: 0,
            expiredLicenses: 0,
            gracePeriodLicenses: 0,
            soldLicenses: 0,
            totalLicenses: 0,
            unsoldLicenses: 0,
            migratedProductsCount: 0,
        };

        if (!isNewProductFormVisible) {
            setData((prevData) => [newRow, ...prevData]);
            setNewProductFormVisible(!isNewProductFormVisible);
        }
    };

    const removeNewProductHandler = (id: number) => {
        console.log("remove new product handler: " + id);
        const data = dataTable.filter((product) => product.id !== id);
        setData(data);
        setNewProductFormVisible(!isNewProductFormVisible);
    };

    const projectsOptionList: OptionList = useMemo(() => {
        return dataTableProps.map(({ name, id }) => {
            return {
                id,
                name,
            };
        }) as OptionList;
    }, [dataTableProps]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text="Actual Products" />

            <SearchBar
                dataHandler={setQuires}
                projectOptions={projectsOptionList}
                searchInput={{
                    placeholder: "Product Name",
                }}
            />

            <div className="table_scroll_wrapper">
                <TableHeader
                    title="List of products"
                    pageSize={pageSize}
                    onAddRowHandler={createNewProductHandler}
                    addPermission={Permissions.PRODUCT_EDIT}
                    dataTableLength={currentPageSize}
                >
                    <PaginationBar
                        pageSize={pageSize}
                        currentPage={pageNumber as number}
                        currentPageHandler={setCurrentPage}
                        totalItems={totalProducts}
                    />
                </TableHeader>

                <Table<ProductTable, PropsProject>
                    className="products_page"
                    data={dataTable}
                    columns={columns}
                    getRowCanExpand={() => true}
                    subRowHandler={removeNewProductHandler}
                    renderSubComponent={renderSubComponent}
                    subComponentProps={dataTableProps}
                />
            </div>
        </div>
    );
};

export default Products;
